import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { CONTROL_CENTRE_API_DOMAIN } from '@/config/constants'
import useAuthenticatedMutation from '@/hooks/useAuthenticatedMutation'
import type { FeatureFlag, FlagStatus } from '@/types/controlCenter'
import { cn } from '@/utils/cn'
import { QUERY_KEYS } from '../utils/queryKeys'
import { labelClass, inputClass } from './shared/modalStyles'
import ModalShell from './shared/ModalShell'

interface EditFlagModalProps {
  flag?: FeatureFlag | null
  onClose: () => void
  clientNameToId?: Record<string, number>
}

const EditFlagModal = ({
  flag,
  onClose,
  clientNameToId = {},
}: EditFlagModalProps) => {
  const queryClient = useQueryClient()
  const isCreate = !flag

  const [name, setName] = useState(flag?.name ?? '')
  const [description, setDescription] = useState(flag?.description ?? '')
  const [status, setStatus] = useState<FlagStatus>(flag?.status ?? 'disabled')
  // Store the client id directly — mirrors the API payload and avoids name-keyed lookups.
  const [clientId, setClientId] = useState<number | null>(
    flag?.client != null
      ? (clientNameToId[flag.client] ?? null)
      : (Object.values(clientNameToId)[0] ?? null),
  )
  useEffect(() => {
    if (clientId === null && Object.keys(clientNameToId).length > 0)
      setClientId(
        flag?.client != null
          ? (clientNameToId[flag.client] ?? Object.values(clientNameToId)[0])
          : Object.values(clientNameToId)[0],
      )
  }, [clientNameToId, clientId, flag?.client])
  const [isGlobal, setIsGlobal] = useState(flag?.global ?? true)

  const mutation = useAuthenticatedMutation({
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.featureFlags,
      })
      onClose()
    },
  })

  const handleSave = () => {
    const payload = {
      name: name.trim() || (isCreate ? 'NEW_FLAG' : flag.name),
      description: description.trim(),
      active: status === 'enabled',
      client_id: clientId,
      global: isGlobal,
    }

    mutation.mutate({
      method: isCreate ? 'post' : 'put',
      url: isCreate
        ? `${CONTROL_CENTRE_API_DOMAIN}/admin/feature_flags`
        : `${CONTROL_CENTRE_API_DOMAIN}/admin/feature_flags/${flag.id}`,
      data: payload,
    })
  }

  return (
    <ModalShell
      title={isCreate ? 'Create Feature Flag' : 'Edit Feature Flag'}
      subtitle={!isCreate ? flag.name : undefined}
      onClose={onClose}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault()
          handleSave()
        }}
        className='max-h-[65vh] space-y-5 overflow-y-auto px-6 py-5'
      >
        <div>
          <label htmlFor='flag-name' className={labelClass}>
            Flag Name *
          </label>
          <input
            id='flag-name'
            required
            aria-required='true'
            className={inputClass}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div>
          <label htmlFor='flag-description' className={labelClass}>
            Description
          </label>
          <textarea
            id='flag-description'
            className={cn(inputClass, 'resize-none')}
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
        <div>
          <label htmlFor='flag-client' className={labelClass}>
            Client *
          </label>
          <select
            id='flag-client'
            required
            aria-required='true'
            className={inputClass}
            value={clientId ?? ''}
            onChange={(e) => setClientId(Number(e.target.value))}
          >
            {Object.entries(clientNameToId).map(([name, id]) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
          </select>
        </div>
        <div className='flex gap-4'>
          <div className='flex-1'>
            <label htmlFor='flag-global' className={labelClass}>
              Global *
            </label>
            <select
              id='flag-global'
              required
              aria-required='true'
              className={inputClass}
              value={isGlobal ? 'true' : 'false'}
              onChange={(e) => setIsGlobal(e.target.value === 'true')}
            >
              <option value='true'>Yes</option>
              <option value='false'>No</option>
            </select>
          </div>
          <div className='flex-1'>
            <label htmlFor='flag-status' className={labelClass}>
              Status *
            </label>
            <select
              id='flag-status'
              required
              aria-required='true'
              className={inputClass}
              value={status}
              onChange={(e) => setStatus(e.target.value as FlagStatus)}
            >
              <option value='enabled'>Enabled</option>
              <option value='disabled'>Disabled</option>
            </select>
          </div>
        </div>

        {mutation.error && (
          <p className='text-sm text-red-400'>{mutation.error.message}</p>
        )}

        <div className='border-border-main flex items-center justify-end gap-3 border-t px-6 py-4'>
          <button
            type='button'
            onClick={onClose}
            className='border-border-main text-text-secondary hover:bg-neutral-dimmed rounded-lg border px-4 py-2 text-sm'
          >
            Cancel
          </button>
          <button
            type='submit'
            disabled={
              name.trim() === '' || clientId === null || mutation.isPending
            }
            className='rounded-lg bg-violet-600 px-5 py-2 text-sm font-semibold text-white hover:bg-violet-500 disabled:cursor-not-allowed disabled:opacity-40'
          >
            {mutation.isPending ? 'Saving…' : isCreate ? 'Create Flag' : 'Save'}
          </button>
        </div>
      </form>
    </ModalShell>
  )
}

export default EditFlagModal
